import { PROPERTIES_API_URL } from '@src/constants/urls';
import { CreatePropertyPayload } from '@src/interfaces/app/agent/addProperty';
import {
  ClosePropertyProps,
  ModifyDocumentsProperty,
  UpdatePropertyPayload,
} from '@src/interfaces/app/agent/edit';
import {
  DeleteUnsaveRenterSyncPayload,
  PostSaveRenterSyncPayload,
} from '@src/interfaces/app/propertyLists';
import {
  httpDelete,
  httpGet,
  httpPatch,
  httpPost,
  httpPut,
} from '@src/utils/http';

/**
 * This method is used to perform API request for retrieving public properties.
 *
 * @returns Promise
 */
const getProperties = async (
  filterURL: string | null,
  usePagination = false,
  limit = 20,
  offset = 0
) => {
  let url = PROPERTIES_API_URL.PUBLIC_PROPERTIES;
  if (filterURL) url += filterURL;
  if (usePagination) {
    url = filterURL
      ? `${url}&limit=${limit}&offset=${offset}`
      : `${url}?limit=${limit}&offset=${offset}`;
  }
  const response = await httpGet(url);

  return response;
};

/**
 * This method is used to perform API request for retrieving public property by id.
 *
 * @returns Promise
 */
const getProperty = async (id: string, increase_view_count?: boolean) => {
  let url = `${PROPERTIES_API_URL.PROPERTY_ID.replace(
    '#ID#',
    String(id || '')
  )}`;

  if (increase_view_count === false) {
    url += `?increase_view_count=${increase_view_count}`;
  }
  const response = await httpGet(url);

  return response;
};

/**
 * This method is used to perform API request for retrieving agent properties.
 *
 * @returns Promise
 */
const getAgentProperties = async (
  agentId: string,
  isSearchURL: string | null,
  offset: number
) => {
  const limit = 20;
  const pageOffset = offset * limit || 0;
  const url = isSearchURL
    ? `${PROPERTIES_API_URL.AGENT_PROPERTIES}/${agentId}${isSearchURL}&limit=${limit}&offset=${pageOffset}`
    : `${PROPERTIES_API_URL.AGENT_PROPERTIES}/${agentId}?limit=${limit}&offset=${pageOffset}`;
  const response = await httpGet(url);

  return response;
};

/**
 * This method is used to perform API request for creating properties.
 * @param FormData
 * @returns Promise
 */
const createProperties = async (params: FormData) => {
  const response = await httpPost(
    `${PROPERTIES_API_URL.PUBLIC_PROPERTIES}`,
    params
  );

  return response;
};

/**
 * This method is used to perform API request for update the properties.
 * @param UpdatePropertyPayload
 * @returns Promise
 */
const updateProperties = async (params: UpdatePropertyPayload) => {
  const { id, payload } = params;
  const response = await httpPatch(
    `${PROPERTIES_API_URL.PROPERTY_ID.replace('#ID#', String(id || ''))}`,
    payload
  );

  return response;
};

/**
 * This method is used to perform API request for update the properties.
 * @param UpdatePropertyPayload
 * @returns Promise
 */
const modifyDocumentsProperties = async (params: ModifyDocumentsProperty) => {
  const { id, payload } = params;
  const response = await httpPut(
    `${PROPERTIES_API_URL.MODIFY_PROPERTY_DOCUMENT.replace(
      '#ID#',
      String(id || '')
    )}`,
    payload
  );

  return response;
};

/**
 * This method is used to perform API request for creating description with chatgpt.
 * @param CreatePropertyPayload
 * @returns Promise
 */
const propertyPreviewBeforePosting = async (params: CreatePropertyPayload) => {
  const response = await httpPost(
    `${PROPERTIES_API_URL.PROPERTY_PREVIEW_BEFORE_POSTING}`,
    params
  );

  return response;
};

const getMapProperties = async (
  agentId = '',
  filterURL: string | null = null
) => {
  let url = PROPERTIES_API_URL.MAP_DATA;
  if (filterURL) {
    url += filterURL;
  }
  const response = await httpGet(
    agentId !== '' ? `${url}?agent_id=${agentId}` : `${url}`
  );
  return response;
};

const openProperty = async (id: string) => {
  const response = await httpPost(
    `${PROPERTIES_API_URL.OPEN_PROPERTY.replace('#ID#', String(id || ''))}`,
    {}
  );
  return response;
};

const closeProperty = async (id: string) => {
  const response = await httpPost(
    `${PROPERTIES_API_URL.CLOSE_PROPERTY.replace('#ID#', String(id || ''))}`,
    {}
  );
  return response;
};

/**
 * This method is used to perform API request for retrieving all save renter property
 *
 * @returns Promise
 */
const getAllRenterSavesProperty = async () => {
  const response = await httpGet(PROPERTIES_API_URL.GET_ALL_SAVE_PROPERTY);
  return response;
};

/**
 * This method is used to perform API request for save renter property.
 * @param {PostSaveRenterSyncPayload} params
 * @returns Promise
 */
const postRenterSaveSync = async (params: PostSaveRenterSyncPayload) => {
  const response = await httpPost(
    PROPERTIES_API_URL.POST_RENTER_SAVE_SYNC,
    params
  );
  return response;
};

/**
 * This method is used to perform API request for unsave renter property.
 * @param {PostSaveRenterSyncPayload} params
 * @returns Promise
 */
const deleteRenterunSaveSync = async (
  params: DeleteUnsaveRenterSyncPayload
) => {
  const response = await httpDelete(
    PROPERTIES_API_URL.DELETE_RENTER_SAVE_SYNC,
    params
  );
  return response;
};

/**
 * Searches for a property based on the provided search query.
 *
 * @param {string} search - The search query to use.
 * @return {Promise<any>} A promise that resolves to the response from the server.
 */
const searchProperty = async (
  search: string,
  restrict: string,
  isAllowGlobalSearch: boolean,
  previousQuery?: string
) => {
  const params: {
    input_query: string;
    restrict?: string;
    global_search?: boolean;
    previous_query?: string;
  } = {
    input_query: search,
  };

  if (isAllowGlobalSearch) {
    params['global_search'] = true;
  }

  if (restrict) {
    params['restrict'] = restrict;
  }

  if (previousQuery) {
    params['previous_query'] = previousQuery;
  }

  const response = await httpPost(PROPERTIES_API_URL.SEARCH_PROPERTY, params);
  return response;
};

/**
 * Retrieves the agent required list template asynchronously.
 *
 * @return {Promise<any>} A promise that resolves to the response from the server.
 */
const getAgentRequiredListTemplate = async () => {
  const response = await httpGet(
    `${PROPERTIES_API_URL.AGENT_TEMPLATE_RETREIVE}`
  );
  return response;
};

/**
 * Retrieves the agent document asynchronously.
 *
 * @return {Promise<any>} A promise that resolves to the response from the server.
 */
const getAgentRequiredListDocuments = async () => {
  const response = await httpGet(`${PROPERTIES_API_URL.AGENT_DOCUMENTS}`);
  return response;
};

const checkDuplicateDocument = async (name: string) => {
  const response = await httpGet(
    `${PROPERTIES_API_URL.CHECK_DUPLICATE_DOCUMENT}?name=${name}`
  );
  return response;
};

const deleteAgentDocument = async (name: string) => {
  const response = await httpDelete(
    `${PROPERTIES_API_URL.DELETE_DOCUMENT}?name=${name}`
  );
  return response;
};

const agentCloseProperty = async (
  propertyId: string,
  params: ClosePropertyProps
) => {
  const url = PROPERTIES_API_URL.AGENT_CLOSE_PROPERTY.replace(
    '#ID#',
    String(propertyId || '')
  );

  const response = await httpPost(url, params);
  return response;
};

export {
  getProperties,
  getProperty,
  getAgentProperties,
  createProperties,
  propertyPreviewBeforePosting,
  updateProperties,
  modifyDocumentsProperties,
  getMapProperties,
  openProperty,
  closeProperty,
  getAllRenterSavesProperty,
  postRenterSaveSync,
  deleteRenterunSaveSync,
  searchProperty,
  getAgentRequiredListTemplate,
  getAgentRequiredListDocuments,
  checkDuplicateDocument,
  deleteAgentDocument,
  agentCloseProperty,
};
